import * as React from 'react';
import {IconButton, InputBase, MenuItem} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import Select from '@components/common/select/Select';
import { styleColors } from 'src/main.style';

const CUSTOM_KEY = '__custom__';

export interface ILocationOption {
    value: string;
    label: string;
    isDisabled?: boolean;
}

interface ILocationSelectProps {
    id?: string;
    name?: string;
    value: string;
    onChange: (value: string) => void;
    baseOptions: ILocationOption[];
    customLocations: string[];
    onAddCustomLocation: (value: string) => void;
    error?: boolean;
    style?: React.CSSProperties;
    native?: boolean;
    placeholder?: string;
    customLabel: string;
    inputPlaceholder: string;
}

const LocationSelect: React.FC<ILocationSelectProps> = ({
    id,
    name,
    value,
    onChange,
    baseOptions,
    customLocations,
    onAddCustomLocation,
    error,
    style,
    native,
    placeholder,
    customLabel,
    inputPlaceholder,
}) => {
    const [isEntering, setIsEntering] = React.useState(false);
    const [inputValue, setInputValue] = React.useState('');
    const suppressBlurRef = React.useRef(false);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const enterStartedAtRef = React.useRef(0);
    const focusTimerRef = React.useRef<number | null>(null);
    const fallbackFocusTimerRef = React.useRef<number | null>(null);
    const blurUnlockTimerRef = React.useRef<number | null>(null);

    const focusInput = React.useCallback((delay = 0, fallback = false) => {
        const timerRef = fallback ? fallbackFocusTimerRef : focusTimerRef;
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
        }

        timerRef.current = window.setTimeout(() => {
            inputRef.current?.focus();
            timerRef.current = null;
        }, delay);
    }, []);

    const startEntering = React.useCallback(() => {
        suppressBlurRef.current = true;
        enterStartedAtRef.current = Date.now();
        setInputValue('');
        setIsEntering(true);

        focusInput(350, true);
        if (blurUnlockTimerRef.current !== null) {
            window.clearTimeout(blurUnlockTimerRef.current);
        }
        blurUnlockTimerRef.current = window.setTimeout(() => {
            suppressBlurRef.current = false;
            blurUnlockTimerRef.current = null;
        }, 700);
    }, [focusInput]);

    React.useEffect(() => {
        if (isEntering) {
            focusInput();
        }
    }, [focusInput, isEntering]);

    React.useEffect(() => () => {
        if (focusTimerRef.current !== null) {
            window.clearTimeout(focusTimerRef.current);
        }
        if (fallbackFocusTimerRef.current !== null) {
            window.clearTimeout(fallbackFocusTimerRef.current);
        }
        if (blurUnlockTimerRef.current !== null) {
            window.clearTimeout(blurUnlockTimerRef.current);
        }
    }, []);

    const handleConfirm = () => {
        const trimmed = inputValue.trim();
        if (trimmed) {
            onAddCustomLocation(trimmed);
            onChange(trimmed);
        }
        setIsEntering(false);
        setInputValue('');
    };

    const handleCancel = () => {
        onChange('Unspecified');
        setIsEntering(false);
        setInputValue('');
    };

    const handleBlurOutside = () => {
        const hasJustOpened = Date.now() - enterStartedAtRef.current < 900;
        if (hasJustOpened) {
            focusInput(150);
            return;
        }

        const trimmed = inputValue.trim();
        if (trimmed.length > 1) {
            onAddCustomLocation(trimmed);
            onChange(trimmed);
        } else {
            onChange('Unspecified');
        }
        setIsEntering(false);
        setInputValue('');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleConfirm();
        }
        if (e.key === 'Escape') handleCancel();
    };

    const allOptions: ILocationOption[] = [
        ...baseOptions,
        ...customLocations.map(loc => ({value: loc, label: loc})),
        {value: CUSTOM_KEY, label: customLabel},
    ];
    const shrinkableStyle: React.CSSProperties = {
        ...style,
        minWidth: 0,
        maxWidth: '100%',
    };

    if (isEntering) {
        return (
            <div
                style={{display: 'flex', alignItems: 'center', gap: 4, flex: 1, ...shrinkableStyle}}
                onBlur={(e) => {
                    if (suppressBlurRef.current) return;
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                        handleBlurOutside();
                    }
                }}
            >
                <InputBase
                    autoFocus={true}
                    inputRef={inputRef}
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={inputPlaceholder}
                    style={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: 40,
                        border: `1px solid ${styleColors.greyDark2}`,
                        borderRadius: 3,
                        padding: '0 12px',
                        fontSize: 16,
                        fontWeight: 600,
                        backgroundColor: styleColors.white,
                        boxSizing: 'border-box',
                    }}
                />
                <IconButton size="small" onClick={handleConfirm} style={{padding: 4}}>
                    <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleCancel} style={{padding: 4}}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </div>
        );
    }

    if (native) {
        return (
            <select
                id={id}
                name={name}
                value={value}
                style={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 40,
                    backgroundColor: styleColors.white,
                    border: `1px solid ${styleColors.greyDark2}`,
                    borderRadius: '0.1875rem',
                    padding: '0 32px 0 12px',
                    fontSize: 16,
                    fontWeight: 600,
                    color: styleColors.greyDark2,
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3e%3cpath d='M7 10l5 5 5-5z'/%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 8px center',
                    backgroundSize: '20px',
                    boxSizing: 'border-box',
                    ...shrinkableStyle,
                } as React.CSSProperties}
                onChange={e => {
                    const selectedValue = e.target.value;
                    if (selectedValue === CUSTOM_KEY) {
                        startEntering();
                    } else if (typeof selectedValue === 'string') {
                        onChange(selectedValue);
                    } else {
                        onChange('Unspecified');
                    }
                }}
            >
                {allOptions.map(opt => (
                    <option key={opt.value} value={opt.value} disabled={opt.isDisabled}>
                        {opt.label}
                    </option>
                ))}
            </select>
        );
    }

    return (
        <Select
            id={id}
            name={name}
            value={value}
            onChange={(e: any) => {
                const val = e.target.value as string;
                if (val === CUSTOM_KEY) {
                    startEntering();
                } else {
                    onChange(val);
                }
            }}
            placeholder={placeholder}
            style={shrinkableStyle}
            error={error}
        >
            {allOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value} disabled={opt.isDisabled}>
                    {opt.label}
                </MenuItem>
            ))}
        </Select>
    );
};

export default LocationSelect;



// WEBPACK FOOTER //
// ./src/components/guide/steps/LocationSelect.tsx